import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	type ShortcutCategory,
	registerShortcut,
	useShortcutRegistry,
} from "@/lib/keyboard";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const CATEGORY_ORDER: ShortcutCategory[] = [
	"global",
	"navigation",
	"editing",
	"module",
];

const CATEGORY_LABELS: Record<ShortcutCategory, string> = {
	global: "shortcuts_group_global",
	navigation: "shortcuts_group_navigation",
	editing: "shortcuts_group_editing",
	module: "shortcuts_group_module",
};

export function KeyboardShortcutsHelp() {
	const [open, setOpen] = useState(false);
	const { t } = useTranslation();
	const shortcuts = useShortcutRegistry();

	// Register the help dialog's own shortcut
	useEffect(() => {
		return registerShortcut({
			id: "global.shortcuts-help",
			keys: "meta+?",
			label: "shortcuts_help",
			displayKeys: "⌘?",
			category: "global",
			action: () => setOpen(true),
		});
	}, []);

	// Group by category
	const grouped = CATEGORY_ORDER.map((cat) => ({
		category: cat,
		label: CATEGORY_LABELS[cat],
		items: shortcuts.filter((s) => s.category === cat),
	})).filter((g) => g.items.length > 0);

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogContent className="max-w-sm">
				<DialogHeader>
					<DialogTitle>{t("keyboard_shortcuts")}</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					{grouped.map((group) => (
						<div key={group.category}>
							<p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 px-1">
								{t(group.label, { defaultValue: group.category })}
							</p>
							<div className="space-y-0.5">
								{group.items.map((s) => (
									<div
										key={s.id}
										className="flex items-center justify-between py-1.5 px-1 rounded hover:bg-muted/50"
									>
										<span className="text-sm text-muted-foreground">
											{t(s.label, { defaultValue: s.id })}
										</span>
										<kbd className="inline-flex h-6 items-center gap-0.5 rounded border bg-muted px-2 font-mono text-xs">
											{s.displayKeys}
										</kbd>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			</DialogContent>
		</Dialog>
	);
}
